/**
 * feat-346: shared rules for Learn drill multiple-choice options.
 *
 * A multiple_choice question has 2–4 options. A and B are required; C and D
 * are optional and filled contiguously (D filled ⇒ C filled). A blank option
 * is sent to the admin API as `''` (never null, never omitted). The correct
 * option must point at a filled option.
 *
 * These mirror the backend's 422 rules (routes/admin_quiz_content.py), so the
 * editors and the Notion importer can make that 422 unreachable. Pure — no
 * React, no service imports — so the parser, importer model and both editors
 * share one copy.
 */

const LETTERS = ['A', 'B', 'C', 'D'] as const;

export type McOptionsProblemCode = 'too_few' | 'gap' | 'correct_blank';

export interface McOptionsProblem {
  code: McOptionsProblemCode;
  /** Index (0..3) of the option the problem is about, for inline errors. */
  index: number;
  message: string;
}

export const MC_OPTIONS_TOO_FEW_MESSAGE = 'Options A and B are required.';
export const MC_CORRECT_BLANK_MESSAGE =
  'The correct answer is blank — pick one of the filled-in options.';

/** "Options must be filled in order — move D up to C." */
export function mcOptionsGapMessage(filledIndex: number, blankIndex: number): string {
  return `Options must be filled in order — move ${LETTERS[filledIndex]} up to ${LETTERS[blankIndex]}.`;
}

const isFilled = (s: string | null | undefined) => (s ?? '').trim() !== '';

/** Normalize a correct-option value (index, 'a'..'d', 'A'..'D') to 0..3 or null. */
function correctIndexOf(correct: number | string | null | undefined): number | null {
  if (correct == null) return null;
  if (typeof correct === 'number') return correct >= 0 && correct <= 3 ? correct : null;
  const i = ['a', 'b', 'c', 'd'].indexOf(correct.toLowerCase());
  return i >= 0 ? i : null;
}

/**
 * First problem with a multiple_choice option set, or null when it is valid.
 * Check order: too few (fewer than 2 filled) → gap (a blank before a filled
 * option) → correct option blank. Pass `correct` as undefined to skip the
 * correct-option check (e.g. when a caller reports "no correct answer" itself).
 */
export function mcOptionsProblem(
  opts: ReadonlyArray<string | null | undefined>,
  correct?: number | string | null,
): McOptionsProblem | null {
  const four = [0, 1, 2, 3].map((i) => opts[i] ?? '');
  const filledCount = four.filter(isFilled).length;
  if (filledCount < 2) {
    const firstBlank = four.findIndex((o) => !isFilled(o));
    return { code: 'too_few', index: firstBlank < 0 ? 0 : firstBlank, message: MC_OPTIONS_TOO_FEW_MESSAGE };
  }
  const firstBlank = four.findIndex((o) => !isFilled(o));
  if (firstBlank >= 0) {
    const laterFilled = four.findIndex((o, i) => i > firstBlank && isFilled(o));
    if (laterFilled >= 0) {
      return { code: 'gap', index: laterFilled, message: mcOptionsGapMessage(laterFilled, firstBlank) };
    }
  }
  if (correct !== undefined) {
    const ci = correctIndexOf(correct);
    if (ci === null || !isFilled(four[ci])) {
      return { code: 'correct_blank', index: ci ?? 0, message: MC_CORRECT_BLANK_MESSAGE };
    }
  }
  return null;
}

/** Indices (0..3) of the filled options — what a correct-answer picker offers. */
export function filledOptionIndices(opts: ReadonlyArray<string | null | undefined>): number[] {
  return [0, 1, 2, 3].filter((i) => isFilled(opts[i]));
}

/** Read-only display helper: keep only filled options, with their letters. */
export function filledOptions<T extends { text: string | null | undefined }>(
  opts: ReadonlyArray<T>,
): T[] {
  return opts.filter((o) => (o.text ?? '').trim() !== '');
}

// ------------------------------------------------------------
// Editor helpers (feat-346) — on-screen copy lives here, not in JSX literals.
// ------------------------------------------------------------

/** Suffix on the C/D option labels for multiple choice. */
export const MC_OPTIONAL_SUFFIX = ' (optional)';
/** Placeholder on a locked D while C is blank. */
export const MC_FILL_C_FIRST = 'Fill in C first';

/** True when option `index` must not be typed into yet: D while C is blank
 *  (and D itself is still empty — a D that already has text stays editable so
 *  the author can clear it or move it up; the gap message explains why it can't save). */
export function isMcOptionLocked(index: number, opts: ReadonlyArray<string | null | undefined>): boolean {
  return index === 3 && !isFilled(opts[2]) && !isFilled(opts[3]);
}

/** Placeholder / visible label for an option input. Fill-blank is unchanged. */
export function mcOptionPlaceholder(
  index: number,
  isMc: boolean,
  opts: ReadonlyArray<string | null | undefined>,
): string {
  const base = `Option ${LETTERS[index]}`;
  if (!isMc) return base;
  if (isMcOptionLocked(index, opts)) return MC_FILL_C_FIRST;
  return index >= 2 ? `${base}${MC_OPTIONAL_SUFFIX}` : base;
}

/** True when an edit takes the CORRECT option from filled to blank — the
 *  editor then clears the pick so the author must choose again. */
export function blanksCorrectOption(
  index: number,
  correctIndex: number | null,
  prev: string | null | undefined,
  next: string,
): boolean {
  return correctIndex === index && isFilled(prev) && !isFilled(next);
}

export { isFilled as isOptionFilled };
